'use client';

import { Spinner } from 'components/Elements/Spinner';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import * as React from 'react';

/**
 * THEME COLORS
 * Primary: #383D4E → #142852
 * Secondary: #FF6937 → #FF3D00
 */

const variants = {
  primary:
    'bg-gradient-to-r from-[#383D4E] to-[#142852] text-white hover:from-[#142852] hover:to-[#1f2d4a]',
  secondary:
    'bg-gradient-to-r from-[#FF6937] to-[#FF3D00] text-white hover:from-[#FF3D00] hover:to-[#E63C00]',
  inverse:
    'bg-[#ffffff] text-[#142852] border border-[#142852] hover:bg-gradient-to-r hover:from-[#383D4E] hover:to-[#142852] hover:text-white',
  danger: 'bg-gradient-to-r from-red-600 to-red-500 text-white hover:from-red-700 hover:to-red-600',
  dark: 'bg-gradient-to-r from-gray-800 to-gray-900 text-white hover:from-gray-900 hover:to-black',
  outline:
    'border border-[#383D4E] text-[#383D4E] hover:bg-gradient-to-r hover:from-[#383D4E] hover:to-[#142852] hover:text-white',
};

const sizes = {
  sm: 'py-2.5 px-4 text-sm font-semibold',
  md: 'py-3 px-6 text-md font-semibold',
  lg: 'py-3.5 px-8 text-lg font-semibold',
};

export const Button = React.forwardRef(
  (
    {
      type = 'button',
      className = '',
      variant = 'primary',
      size = 'md',
      isLoading = false,
      startIcon,
      endIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <motion.button
        ref={ref}
        type={type}
        disabled={disabled}
        whileHover={{ scale: disabled ? 1 : 1.03 }}
        whileTap={{ scale: disabled ? 1 : 0.97 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        className={clsx(
          'flex items-center justify-center rounded-xl disabled:opacity-60 disabled:cursor-not-allowed shadow-md select-none focus:outline-none',
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {isLoading && <Spinner size="sm" className="text-current" />}
        {!isLoading && startIcon}
        <span className="mx-2">{children}</span>
        {!isLoading && endIcon}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

Button.propTypes = {
  type: PropTypes.string,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'inverse', 'danger', 'dark', 'outline']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  isLoading: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
};
